import { FormEvent, useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import { DEFAULT_EDITOR_PERMISSIONS } from '../../../lib/admin-permissions';
import { useAuth } from '../../../lib/auth-context';
import type { User } from '../../../lib/types';

/* ==========================================================================
   GESTÃO DE USUÁRIOS E PERMISSÕES (Tabela admins)
   ========================================================================== */

/**
 * Criação, edição de credenciais, controle de permissões e desativação
 * de administradores e editores da plataforma.
 */
export function UsersEditor() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [editing, setEditing] = useState<(Partial<User> & { password?: string }) | null>(null);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  /**
   * Busca a lista de usuários cadastrados
   */
  async function load(): Promise<void> {
    try {
      const data: User[] = await api.users();
      setUsers(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar usuários.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  /**
   * Inicializa o formulário para um novo usuário editor
   */
  const newUser = (): void => {
    setEditing({
      email: '',
      role: 'editor',
      active: 1,
      permissions: [...DEFAULT_EDITOR_PERMISSIONS],
      password: '',
    });
  };

  /**
   * Alterna a permissão individual de um editor
   */
  const togglePermission = (permission: string): void => {
    setEditing((value) =>
      value
        ? {
            ...value,
            permissions: value.permissions?.includes(permission)
              ? value.permissions.filter((item) => item !== permission)
              : [...(value.permissions ?? []), permission],
          }
        : value
    );
  };

  /**
   * Salva (cria ou edita) o usuário no backend
   */
  const save = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!editing?.email) return;

    setSaving(true);
    setError('');

    try {
      const payload: Record<string, unknown> = {
        email: editing.email,
        role: editing.role,
        active: Boolean(editing.active),
        permissions: editing.role === 'admin' ? ['*'] : editing.permissions ?? [],
      };

      if (editing.password) payload.password = editing.password;

      if (editing.id) {
        await api.updateUser(editing.id, payload);
      } else {
        await api.createUser({ ...payload, password: editing.password });
      }

      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível salvar o usuário.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Desativa o acesso de um usuário existente
   */
  const disable = async (id: string): Promise<void> => {
    if (!window.confirm('Desativar este usuário?')) return;
    try {
      await api.removeUser(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível desativar.');
    }
  };

  return (
    <section className="admin-section" aria-labelledby="users-title">
      <div className="admin-section-head">
        <div>
          <span className="section-tag">ACESSO</span>
          <h2 id="users-title">Usuários e privilégios</h2>
          <p className="muted">Crie administradores, editores e contas com acesso limitado.</p>
        </div>
        <button className="btn btn-primary" onClick={newUser}>
          Novo usuário
        </button>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}

      {editing && (
        <form className="admin-editor" onSubmit={save}>
          <div className="admin-form-grid">
            <label>
              E-mail
              <input
                type="email"
                required
                value={editing.email ?? ''}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
              />
            </label>
            <label>
              Senha {editing.id ? '(deixe vazia para manter)' : '(mínimo 12 caracteres)'}
              <input
                type="password"
                autoComplete="new-password"
                required={!editing.id}
                minLength={12}
                value={editing.password ?? ''}
                onChange={(e) => setEditing({ ...editing, password: e.target.value })}
              />
            </label>
            <label>
              Perfil
              <select
                value={editing.role ?? 'editor'}
                onChange={(e) => setEditing({ ...editing, role: e.target.value as User['role'] })}
              >
                <option value="editor">Editor</option>
                <option value="admin">Administrador</option>
              </select>
            </label>
            <label>
              Ativo
              <input
                type="checkbox"
                checked={Boolean(editing.active)}
                disabled={editing.id === currentUser?.id}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked ? 1 : 0 })}
              />
            </label>
          </div>

          {editing.role !== 'admin' && (
            <fieldset className="permission-grid">
              <legend>Permissões do editor</legend>
              {DEFAULT_EDITOR_PERMISSIONS.map((permission) => (
                <label key={permission}>
                  <input
                    type="checkbox"
                    checked={editing.permissions?.includes(permission) ?? false}
                    onChange={() => togglePermission(permission)}
                  />
                  <span>{permission}</span>
                </label>
              ))}
            </fieldset>
          )}

          {editing.role === 'admin' && (
            <p className="form-info">
              Administradores possuem acesso total ao CMS e à gestão de usuários.
            </p>
          )}

          <div className="admin-form-actions">
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar usuário'}
            </button>
            <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Usuário</th>
              <th>Perfil</th>
              <th>Status</th>
              <th>Permissões</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map((item) => (
              <tr key={item.id}>
                <td>
                  <strong>{item.email}</strong>
                  <small>{item.createdAt ? new Date(item.createdAt).toLocaleString('pt-BR') : ''}</small>
                </td>
                <td>{item.role === 'admin' ? 'Administrador' : 'Editor'}</td>
                <td>{item.active ? 'Ativo' : 'Desativado'}</td>
                <td>{item.permissions.includes('*') ? 'Todas' : `${item.permissions.length} permissões`}</td>
                <td className="table-actions">
                  <button className="btn btn-small btn-outline" onClick={() => setEditing({ ...item, password: '' })}>
                    Editar
                  </button>
                  {item.id !== currentUser?.id && item.active ? (
                    <button className="btn btn-small btn-danger" onClick={() => void disable(item.id)}>
                      Desativar
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}